"use client";

import React from "react";
import { ShieldCheck, FileCheck2, Award, CircleCheck as CheckCircle2, ClipboardList, FileText, ArrowRight, Sparkles } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import Reveal from "@/components/common/Reveal";
import { useLanguage } from "@/hooks/useLanguage";
import { pick } from "@/lib/i18n";
import type { L6 } from "@/lib/commonCopy";
interface ComplianceStandard {
  id: string;
  code: string;
  title: L6;
  description: L6;
  icon: React.ComponentType<{
    className?: string;
  }>;
  highlights: L6[];
}
const COMPLIANCE_STANDARDS: ComplianceStandard[] = [{
  id: "fda",
  code: "21 CFR 177.2600",
  title: {
    en: "FDA Food Contact Compliance",
    zh: "FDA 食品接触合规",
    es: "Cumplimiento FDA para contacto con alimentos",
    de: "FDA-Lebensmittelkontaktkonformität",
    fr: "Conformité contact alimentaire FDA",
    pt: "Conformidade FDA para contato com alimentos"
  },
  description: {
    en: "High-grade 316L stainless steel woven mesh meeting FDA guidelines for direct contact with food, beverages, and active pharmaceutical ingredients.",
    zh: "优质 316L 不锈钢编织网，符合与食品、饮料及原料药直接接触的 FDA 相关指导原则。",
    es: "Malla tejida de acero inoxidable 316L de alta calidad que cumple las pautas de la FDA para contacto directo con alimentos, bebidas e ingredientes farmacéuticos activos.",
    de: "Hochwertiges Drahtgewebe aus Edelstahl 316L gemäß FDA-Richtlinien für den direkten Kontakt mit Lebensmitteln, Getränken und pharmazeutischen Wirkstoffen.",
    fr: "Toile tissée en acier inoxydable 316L de haute qualité conforme aux directives FDA pour contact direct avec aliments, boissons et principes actifs pharmaceutiques.",
    pt: "Malha tecida de aço inoxidável 316L de alta qualidade atendendo às diretrizes da FDA para contato direto com alimentos, bebidas e ingredientes farmacêuticos ativos."
  },
  icon: ShieldCheck,
  highlights: [{
    en: "Non-leaching contact surface",
    zh: "无析出介质接触表面",
    es: "Superficie de contacto sin lixiviación",
    de: "Auslaugungsfreie Kontaktoberfläche",
    fr: "Surface de contact sans lixiviation",
    pt: "Superfície de contato sem lixiviação"
  }, {
    en: "Passivated corrosion resistance",
    zh: "化学钝化耐腐蚀处理",
    es: "Resistencia a la corrosión pasivada",
    de: "Passivierte Korrosionsbeständigkeit",
    fr: "Résistance à la corrosion passivée",
    pt: "Resistência à corrosão passivada"
  }, {
    en: "Zero organic residue",
    zh: "零有机加工残留物",
    es: "Cero residuos orgánicos",
    de: "Keine organischen Rückstände",
    fr: "Zéro résidu organique",
    pt: "Zero resíduo orgânico"
  }]
}, {
  id: "ec1935",
  code: "EC 1935/2004",
  title: {
    en: "EU Food Contact Regulation",
    zh: "欧盟食品接触材料合规",
    es: "Reglamento de la UE sobre contacto con alimentos",
    de: "EU-Lebensmittelkontaktverordnung",
    fr: "Règlement européen sur les matériaux en contact avec les aliments",
    pt: "Regulamento da UE para contato com alimentos"
  },
  description: {
    en: "Suitable for food processing equipment across European markets, supported by Declarations of Compliance (DoC) when requested.",
    zh: "适用于面向欧洲市场的食品加工装备，可按要求提供符合性声明（DoC）。",
    es: "Apto para equipos de procesamiento de alimentos en mercados europeos, respaldado por declaraciones de conformidad (DoC) previa solicitud.",
    de: "Geeignet für Lebensmittelverarbeitungsanlagen im europäischen Markt, unterstützt durch Konformitätserklärungen (DoC) auf Anfrage.",
    fr: "Adapté aux équipements de transformation alimentaire du marché européen, appuyé par des déclarations de conformité (DoC) sur demande.",
    pt: "Adequado para equipamentos de processamento de alimentos nos mercados europeus, com suporte de Declarações de Conformidade (DoC) quando solicitado."
  },
  icon: Award,
  highlights: [{
    en: "EU Framework regulation aligned",
    zh: "符合欧盟食品接触框架要求",
    es: "Alineado con el reglamento marco de la UE",
    de: "Konform mit EU-Rahmenverordnung",
    fr: "Conforme au règlement-cadre de l'UE",
    pt: "Alinhado com o regulamento-quadro da UE"
  }, {
    en: "Material safety documentation",
    zh: "完备的材质安全证明文件",
    es: "Documentación de seguridad del material",
    de: "Material-Sicherheitsdokumentation",
    fr: "Documentation de sécurité des matériaux",
    pt: "Documentação de segurança do material"
  }, {
    en: "Safe for multi-stage processing",
    zh: "保障多级工艺卫生流转",
    es: "Seguro para procesos multietapa",
    de: "Sicher für mehrstufige Prozesse",
    fr: "Sûr pour procédés multi-étapes",
    pt: "Seguro para processos em múltiplos estágios"
  }]
}, {
  id: "usp",
  code: "USP Class VI",
  title: {
    en: "Pharma Biocompatibility Reference",
    zh: "制药生物相容性参考",
    es: "Referencia de biocompatibilidad farmacéutica",
    de: "Pharma-Biokompatibilitäts-Referenz",
    fr: "Référence de biocompatibilité pharmaceutique",
    pt: "Referência de biocompatibilidade farmacêutica"
  },
  description: {
    en: "Material suitability for high-purity biopharmaceutical applications, parenteral solutions, and API filtration.",
    zh: "适用于高纯度生物制药、注射剂溶液过滤与活性药物原料分离的材质规范。",
    es: "Idoneidad del material para aplicaciones biofarmacéuticas de alta pureza, soluciones parenterales y filtración de API.",
    de: "Materialeignung für hochreine biopharmazeutische Anwendungen, parenterale Lösungen und API-Filtration.",
    fr: "Aptitude du matériau pour applications biopharmaceutiques haute pureté, solutions parentérales et filtration de principes actifs.",
    pt: "Adequação do material para aplicações biofarmacêuticas de alta pureza, soluções parenterais e filtração de IFA."
  },
  icon: FileCheck2,
  highlights: [{
    en: "Biological inertness for media",
    zh: "流体介质生物惰性表现",
    es: "Inercia biológica para medios",
    de: "Biologische Inertheit für Medien",
    fr: "Inertie biologique pour milieux",
    pt: "Inércia biológica para meios"
  }, {
    en: "Low extractables & leachables",
    zh: "超低可萃取与可浸出物风险",
    es: "Bajo nivel de extraíbles y lixiviables",
    de: "Geringe extrahierbare und auslaugbare Stoffe",
    fr: "Faible teneur en extractibles et relargables",
    pt: "Baixo nível de extraíveis e lixiviáveis"
  }, {
    en: "Clean processing compatibility",
    zh: "适配高洁净度流体过滤",
    es: "Compatibilidad con procesamiento limpio",
    de: "Kompatibel mit Reinraumverarbeitung",
    fr: "Compatibilité avec procédés propres",
    pt: "Compatibilidade com processamento limpo"
  }]
}, {
  id: "en10204",
  code: "EN 10204 3.1",
  title: {
    en: "Material Traceability (Optional)",
    zh: "材质追溯报告（按需提供）",
    es: "Trazabilidad del material (opcional)",
    de: "Material-Rückverfolgbarkeit (optional)",
    fr: "Traçabilité matière (en option)",
    pt: "Rastreabilidade de material (opcional)"
  },
  description: {
    en: "Heat code tracking and steel mill material test reports available when specified in the order.",
    zh: "炉号追踪及钢厂材质检验报告可按订单要求提供。",
    es: "Trazabilidad por colada e informes de prueba de fábrica de acero disponibles cuando se especifican en el pedido.",
    de: "Schmelzenrückverfolgung und Werkszeugnisse auf Anfrage bei Auftragserteilung erhältlich.",
    fr: "Traçabilité des coulées et certificats d'essais d'aciérie disponibles lorsque spécifiés à la commande.",
    pt: "Rastreabilidade por corrida e relatórios de ensaio de usina disponíveis quando especificados no pedido."
  },
  icon: ClipboardList,
  highlights: [{
    en: "Chemical composition breakdown",
    zh: "化学成分光谱分析数据",
    es: "Desglose de composición química",
    de: "Aufschlüsselung der chemischen Zusammensetzung",
    fr: "Détail de composition chimique",
    pt: "Detalhamento da composição química"
  }, {
    en: "Mechanical tensile verification",
    zh: "力学抗拉强度测试数据",
    es: "Verificación de tracción mecánica",
    de: "Mechanische Zugfestigkeitsprüfung",
    fr: "Vérification mécanique en traction",
    pt: "Verificação mecânica de tração"
  }, {
    en: "Heat-lot unique identification",
    zh: "单批次炉号溯源标识",
    es: "Identificación única de lote de colada",
    de: "Eindeutige Schmelzen-Identifikation",
    fr: "Identification unique du lot de coulée",
    pt: "Identificação única do lote de corrida"
  }]
}, {
  id: "sanitary",
  code: "3-A & EHEDG",
  title: {
    en: "Sanitary Engineering Design",
    zh: "卫生级工程设计规范",
    es: "Diseño de ingeniería sanitaria",
    de: "Hygienegerechtes Konstruktionsdesign",
    fr: "Conception d'ingénierie sanitaire",
    pt: "Projeto de engenharia sanitária"
  },
  description: {
    en: "Crevice-free welded construction and smooth surface finishes (Ra < 0.4 µm) designed for CIP (Clean-In-Place) and SIP protocols.",
    zh: "无死角全熔透焊接与平滑表面处理（Ra < 0.4 µm），专为在线清洗（CIP）与原位灭菌（SIP）工艺设计。",
    es: "Construcción soldada sin ranuras y acabados superficiales lisos (Ra < 0.4 µm) diseñados para protocolos CIP y SIP.",
    de: "Spaltfreie Schweißkonstruktion und glatte Oberflächengüten (Ra < 0.4 µm), ausgelegt für CIP- und SIP-Protokolle.",
    fr: "Construction soudée sans interstices et finitions de surface lisses (Ra < 0.4 µm) conçues pour protocoles NEP (CIP) et SEP (SIP).",
    pt: "Construção soldada sem frestas e acabamentos superficiais lisos (Ra < 0.4 µm) projetados para protocolos CIP e SIP."
  },
  icon: CheckCircle2,
  highlights: [{
    en: "Smooth Ra finish options",
    zh: "多种平滑 Ra 表面精度可选",
    es: "Opciones de acabado liso Ra",
    de: "Glatte Ra-Oberflächenoptionen",
    fr: "Options de finitions lisses Ra",
    pt: "Opções de acabamento liso Ra"
  }, {
    en: "Crevice-free seam welding",
    zh: "无滞留死角焊缝工艺",
    es: "Soldadura de costura sin ranuras",
    de: "Spaltfreie Nahtschweißung",
    fr: "Soudure de joint sans interstice",
    pt: "Soldagem de costura sem frestas"
  }, {
    en: "Optimized CIP/SIP cleanability",
    zh: "优化在线清洗与灭菌效率",
    es: "Limpiabilidad CIP/SIP optimizada",
    de: "Optimierte CIP/SIP-Reinigbarkeit",
    fr: "Nettoyabilité NEP/SEP optimisée",
    pt: "Limpabilidade CIP/SIP otimizada"
  }]
}, {
  id: "iso",
  code: "ASTM E2016",
  title: {
    en: "Weave Quality Reference",
    zh: "工业编织品质参考标准",
    es: "Referencia de calidad de tejido",
    de: "Referenz für Webqualität",
    fr: "Référence de qualité de tissage",
    pt: "Referência de qualidade de tecelagem"
  },
  description: {
    en: "Reference standard for wire diameter uniformity, aperture accuracy, and weave integrity.",
    zh: "丝径均匀性、网孔精度及编织整体结构完整性的公认参考标准。",
    es: "Norma de referencia para uniformidad del diámetro del alambre, precisión de abertura e integridad del tejido.",
    de: "Referenznorm für Gleichmäßigkeit des Drahtdurchmessers, Maschengenauigkeit und Gewebeintegrität.",
    fr: "Norme de référence pour l'uniformité du diamètre du fil, la précision d'ouverture et l'intégrité du tissage.",
    pt: "Norma de referência para uniformidade do diâmetro do arame, precisão de abertura e integridade da malha."
  },
  icon: FileText,
  highlights: [{
    en: "ASTM E2016 verified mesh",
    zh: "符合 ASTM E2016 几何规范",
    es: "Malla verificada según ASTM E2016",
    de: "Gewebe nach ASTM E2016 geprüft",
    fr: "Toile vérifiée selon ASTM E2016",
    pt: "Malha verificada segundo ASTM E2016"
  }, {
    en: "Aperture optical inspection",
    zh: "光学投影孔径校准检验",
    es: "Inspección óptica de abertura",
    de: "Optische Öffnungsprüfung",
    fr: "Contrôle optique de l'ouverture",
    pt: "Inspeção óptica de abertura"
  }, {
    en: "Calibrated pressure testing",
    zh: "校准压力耐受性验证",
    es: "Pruebas de presión calibradas",
    de: "Kalibrierte Druckprüfung",
    fr: "Essais de pression étalonnés",
    pt: "Testes de pressão calibrados"
  }]
}];
const VALIDATION_DOCUMENTS: L6[] = [{
  en: "EN 10204 3.1 Chemical & Mechanical Mill Test Reports (MTR, when specified)",
  zh: "EN 10204 3.1 化学与力学钢厂质保书（MTR，按订单约定提供）",
  es: "Informes de prueba de fábrica químicos y mecánicos EN 10204 3.1 (MTR, cuando se especifique)",
  de: "EN 10204 3.1 Chemische & mechanische Werkszeugnisse (MTR, sofern vereinbart)",
  fr: "Certificats d'essais d'aciérie chimiques et mécaniques EN 10204 3.1 (MTR, si spécifié)",
  pt: "Relatórios de ensaio de usina químicos e mecânicos EN 10204 3.1 (MTR, quando especificado)"
}, {
  en: "Certificate of Conformance (CoC) & FDA / EU Compliance Declarations",
  zh: "合格证书（CoC）及 FDA / 欧盟食品接触符合性声明",
  es: "Certificado de conformidad (CoC) y declaraciones de cumplimiento FDA / UE",
  de: "Konformitätsbescheinigung (CoC) & FDA / EU-Konformitätserklärungen",
  fr: "Certificat de conformité (CoC) et déclarations de conformité FDA / UE",
  pt: "Certificado de conformidade (CoC) e declarações de conformidade FDA / UE"
}, {
  en: "Surface Roughness (Ra) Metrology & Passivation Inspection Records",
  zh: "表面粗糙度（Ra）检测报告与化学钝化检测记录",
  es: "Metrología de rugosidad superficial (Ra) y registros de inspección de pasivado",
  de: "Oberflächenrauheits-Messprotokolle (Ra) & Passivierungs-Prüfberichte",
  fr: "Rapports de métrologie de rugosité (Ra) et contrôles de passivation",
  pt: "Metrologia de rugosidade superficial (Ra) e registros de inspeção de passivação"
}, {
  en: "Bubble Point Integrity & Micron Rating Verification Reports",
  zh: "气泡点结构完整性测试与过滤精度验证报告",
  es: "Informes de verificación de integridad por punto de burbuja y clasificación en micras",
  de: "Prüfberichte für Blaspunkt-Integrität und Mikron-Filterfeinheit",
  fr: "Rapports d'intégrité au point de bulle et vérification du seuil en microns",
  pt: "Relatórios de verificação de integridade por ponto de bolha e retenção em mícrons"
}, {
  en: "BSE/TSE Animal Derivative Free Manufacturing Declaration",
  zh: "无动物源成分衍生接触制造声明（BSE/TSE Free）",
  es: "Declaración de fabricación libre de derivados animales BSE/TSE",
  de: "BSE/TSE-Freiheitserklärung für tierische Derivate in der Fertigung",
  fr: "Déclaration de fabrication exempte de dérivés animaux (ESB/EST)",
  pt: "Declaração de fabricação livre de derivados animais (BSE/TSE)"
}];
const copy = {
  badge: {
    en: "Quality & Regulatory Assurance",
    zh: "质量与合规保障",
    es: "Garantía de calidad y regulación",
    de: "Qualitäts- und Regulierungssicherung",
    fr: "Assurance qualité et réglementaire",
    pt: "Garantia de qualidade e regulatória"
  },
  title: {
    en: "Food & Pharmaceutical Compliance Standards",
    zh: "食品与制药行业合规参考标准",
    es: "Normas de cumplimiento para alimentos y farmacia",
    de: "Compliance-Standards für Lebensmittel und Pharma",
    fr: "Normes de conformité alimentaire et pharmaceutique",
    pt: "Padrões de conformidade para alimentos e farmácia"
  },
  lead: {
    en: "Our stainless steel filtration mesh and custom engineered components comply with rigorous global sanitary standards, supporting clean process flows, heat-lot traceability, and audit documentation for critical processes.",
    zh: "我们制造的不锈钢过滤丝网与定制工程组件遵循严苛的全球卫生制造参考规范，支持高洁净工艺流转、炉号追溯以及关键流程的审计支持。",
    es: "Nuestra malla filtrante de acero inoxidable y componentes personalizados cumplen con exigentes normas sanitarias internacionales, respaldando flujos de proceso limpios, trazabilidad de coladas y documentación de auditoría.",
    de: "Unser Edelstahl-Filtergewebe und maßgefertigte Komponenten entsprechen anspruchsvollen weltweiten Hygienestandards und unterstützen saubere Prozessabläufe, Schmelzenrückverfolgbarkeit und Auditdokumentation.",
    fr: "Notre toile filtrante en acier inoxydable et nos composants sur mesure répondent à des normes sanitaires mondiales rigoureuses, favorisant des procédés propres, la traçabilité des coulées et la documentation d'audit.",
    pt: "Nossa malha filtrante de aço inoxidável e componentes sob medida atendem a rigorosos padrões sanitários globais, suportando fluxos de processo limpos, rastreabilidade de corrida e documentação de auditoria."
  },
  calloutBadge: {
    en: "Audit-Ready Documentation",
    zh: "支持审计核查的文件包",
    es: "Documentación lista para auditorías",
    de: "Auditfähige Dokumentation",
    fr: "Documentation prête pour les audits",
    pt: "Documentação pronta para auditorias"
  },
  calloutHeading: {
    en: "Comprehensive Validation Data Package",
    zh: "完备的技术验证文件包",
    es: "Paquete completo de datos de validación",
    de: "Umfassendes Validierungsdatenpaket",
    fr: "Dossier complet de données de validation",
    pt: "Pacote abrangente de dados de validação"
  },
  calloutLead: {
    en: "To streamline your internal quality management and regulatory submissions, MutualFilter supplies a comprehensive validation documentation package tailored to your exact purchase specification.",
    zh: "为协助客户顺畅推进内部质量管理和合规认证申报，MutualFilter 可根据您的具体采购技术规格提供针对性的验证支持文件包。",
    es: "Para agilizar su gestión de calidad interna y presentaciones regulatorias, MutualFilter suministra un paquete de documentación de validación adaptado a su especificación exacta.",
    de: "Zur Unterstützung Ihres internen Qualitätsmanagements und regulatorischer Einreichungen stellt MutualFilter auf Ihre Spezifikation abgestimmte Validierungsunterlagen bereit.",
    fr: "Pour faciliter votre gestion interne de la qualité et vos dossiers réglementaires, MutualFilter fournit un dossier de validation adapté à vos spécifications d'achat exactes.",
    pt: "Para agilizar sua gestão interna de qualidade e submissões regulatórias, a MutualFilter fornece um pacote de documentação de validação adaptado à sua especificação exata."
  },
  customCardTitle: {
    en: "Need Custom Validation Records?",
    zh: "需要定制验证报告？",
    es: "¿Necesita registros de validación personalizados?",
    de: "Benötigen Sie individuelle Validierungsberichte?",
    fr: "Besoin de rapports de validation personnalisés ?",
    pt: "Precisa de registros de validação personalizados?"
  },
  customCardDesc: {
    en: "Our technical team provides batch-specific test data, custom permeability reports, and material verification documentation for OEM projects upon request.",
    zh: "我们的工程团队可按项目需求为 OEM 客户提供批次检验数据、透气/透水特性报告以及材质复验文件支持。",
    es: "Nuestro equipo técnico proporciona datos de prueba específicos de lote, informes de permeabilidad y documentación de verificación de material para proyectos OEM.",
    de: "Unser technisches Team stellt chargenbezogene Prüfdaten, individuelle Permeabilitätsberichte und Materialnachweise für OEM-Projekte bereit.",
    fr: "Notre équipe technique fournit des données d'essais spécifiques aux lots, des rapports de perméabilité et des documents de vérification de matière pour projets OEM.",
    pt: "Nossa equipe técnica fornece dados de teste específicos por lote, relatórios de permeabilidade e documentação de verificação de material para projetos OEM."
  },
  ctaButton: {
    en: "Request Validation Data",
    zh: "索取验证文件",
    es: "Solicitar datos de validación",
    de: "Validierungsdaten anfordern",
    fr: "Demander les données de validation",
    pt: "Solicitar dados de validação"
  },
  ctaNote: {
    en: "Technical review and specification confirmation by our engineering desk.",
    zh: "由我们的工程团队进行技术核算与规格确认。",
    es: "Revisión técnica y confirmación de especificaciones por nuestro equipo de ingeniería.",
    de: "Technische Prüfung und Spezifikationsbestätigung durch unser Ingenieurteam.",
    fr: "Examen technique et confirmation des spécifications par notre équipe d'ingénierie.",
    pt: "Revisão técnica e confirmação de especificações pela nossa equipe de engenharia."
  }
};
export default function Compliance() {
  const {
    lang
  } = useLanguage();
  return <section id="compliance" data-nav="light" className="bg-background text-foreground relative overflow-hidden mf-section">
      <div className="mf-container">
        {/* Section Header */}
        <Reveal>
          <div className="mx-auto max-w-3xl text-center">
            <Badge variant="outline" className="mb-4 border-primary/20 bg-primary/5 text-primary">
              {pick(lang, copy.badge)}
            </Badge>
            <h2 className="mf-h2 text-foreground">
              {pick(lang, copy.title)}
            </h2>
            <p className="mt-4 mf-lead text-muted-foreground">
              {pick(lang, copy.lead)}
            </p>
          </div>
        </Reveal>

        {/* Standards Grid */}
        <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {COMPLIANCE_STANDARDS.map((standard, index) => {
          const IconComponent = standard.icon;
          return <Reveal key={standard.id} delay={Math.min(index * 60, 300)}>
                <Card data-index={index} className="group relative flex h-full flex-col justify-between border-border bg-card transition-colors duration-200 hover:border-primary/40 rounded-lg">
                  <CardHeader className="p-6 pb-4">
                    <div className="flex items-center justify-between">
                      <div className="flex size-11 items-center justify-center rounded-lg bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                        <IconComponent className="size-5" />
                      </div>
                      <Badge variant="outline" className="bg-muted text-foreground font-mono mf-caption font-semibold">
                        {standard.code}
                      </Badge>
                    </div>
                    <CardTitle className="mt-4 text-xl font-bold text-foreground">
                      {pick(lang, standard.title)}
                    </CardTitle>
                    <CardDescription className="mt-2 text-sm leading-relaxed text-muted-foreground">
                      {pick(lang, standard.description)}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="p-6 pt-0">
                    <div className="border-t border-border pt-4">
                      <ul className="space-y-2">
                        {standard.highlights.map((highlight, hIndex) => <li key={hIndex} className="flex items-center mf-caption font-medium text-foreground/80">
                            <span className="mr-2 size-1.5 rounded-full bg-primary shrink-0" />
                            <span>{pick(lang, highlight)}</span>
                          </li>)}
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </Reveal>;
        })}
        </div>

        {/* Validation Documentation Package Callout */}
        <Reveal delay={120}>
          <div className="mt-12 overflow-hidden rounded-lg border border-border bg-muted/50 p-6 sm:p-8 lg:p-10">
            <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-12">
              <div className="lg:col-span-7">
                <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 mf-caption font-semibold text-primary">
                  <Sparkles className="size-3.5" />
                  {pick(lang, copy.calloutBadge)}
                </div>
                <h3 className="mt-3 mf-h3 text-foreground">
                  {pick(lang, copy.calloutHeading)}
                </h3>
                <p className="mt-3 text-base text-muted-foreground leading-relaxed">
                  {pick(lang, copy.calloutLead)}
                </p>

                <div className="mt-6 space-y-3">
                  {VALIDATION_DOCUMENTS.map((doc, docIdx) => <div key={docIdx} className="flex items-start gap-3">
                      <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-primary" />
                      <span className="text-sm font-medium text-foreground">
                        {pick(lang, doc)}
                      </span>
                    </div>)}
                </div>
              </div>

              <div className="flex flex-col items-start justify-center rounded-lg border border-border bg-card p-6 lg:col-span-5 lg:p-8">
                <h4 className="text-lg font-semibold text-foreground">
                  {pick(lang, copy.customCardTitle)}
                </h4>
                <p className="mt-2 text-sm text-muted-foreground">
                  {pick(lang, copy.customCardDesc)}
                </p>

                <div className="mt-6 w-full space-y-3">
                  <Button size="lg" className="w-full font-medium" asChild>
                    <Link to="/RequestAQuotePage">
                      <span>{pick(lang, copy.ctaButton)}</span>
                      <ArrowRight className="ml-2 size-4" />
                    </Link>
                  </Button>
                  <p className="text-center mf-caption text-muted-foreground">
                    {pick(lang, copy.ctaNote)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>;
}